import { useState, useEffect } from "react";
import { MdArrowDropDown } from "react-icons/md";
import { FiTrash2 } from "react-icons/fi";
import { FaSpinner, FaCheckCircle, FaTimesCircle, FaExclamationCircle } from "react-icons/fa";
import { DataState } from "../../context/DataProvider";

import { API_URL, fetchWithHandling } from "../../services/api";
import { useWebSocket } from "../../context/WebSocketProvider";
import { toast } from "react-hot-toast";

import MarkdownRenderer from "../MarkdownRenderer";

export default function ActionsPanel({ messageId, actionItems = [] , isOutlook = false}) {
  const { userGoogleId } = DataState();
  const [actions, setActions] = useState([]);
  const { subscribe, isConnected } = useWebSocket();
  const [loading, setLoading] = useState(false);
  const [openItems, setOpenItems] = useState({});
  // For new actions
  const [newActionText, setNewActionText] = useState("");
  // For storing approval decisions per action index and per email key.
  // Each decision now includes the original email_arguments.
  const [approvalDecisions, setApprovalDecisions] = useState({});

  // Fetch actions from the backend
  const fetchActions = async () => {
    try {
      setLoading(true);
      // const data = await fetchWithHandling(`${API_URL}/outlook_mails/emails/${messageId}/actions`, {
      const data = await fetchWithHandling(`${API_URL}/actions/email/${isOutlook ? "microsoft_outlook" : "gmail"}/${messageId}/actions`, {
        headers: {
          "Authorization": `Bearer ${userGoogleId}`
        }
      });
      // if (!res.ok) {
      //   console.error("Error fetching actions");
      //   return;
      // }
      // // const data = await res.json();
      setActions(data);
    } catch (error) {
      toast.error(error)
      console.error(error);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => {
    if (!isConnected || !messageId) return;
    const eventKey = `email_action_status_notification_${messageId}`;
    const unsubscribe = subscribe({
      [eventKey]: (notification) => {
        // Expected notification format:
        // { status, action_index, approvalNeededEmails, action_final_response }
        setActions((prevActions) =>
          prevActions.map((action, idx) => {
            if (idx === notification.action_index) {
              return {
                ...action,
                status: notification.status,
                approvalNeededEmails: notification.approvalNeededEmails,
                finalResponse: notification.action_final_response,
              };
            }
            return action;
          })
        );
      },
    });
    return () => {
      unsubscribe();
    };
  }, [isConnected, messageId, subscribe]);


  // // Poll for updates every 5 seconds
  useEffect(() => {
    fetchActions();
    // const interval = setInterval(fetchActions, 5000);
    // return () => clearInterval(interval);
  }, [messageId]);

  const toggleItem = (index) => {
    setOpenItems((prev) => ({ ...prev, [index]: !prev[index] }));
  };

  // Only allow adding a new action if there are no actions,
  // or if the last action's status is "success"
  const canAddAction =
    actions.length === 0 || actions[actions.length - 1].status === "success" || actions[actions.length - 1].status === "failed";

  const addAction = async () => {
    setLoading(true);
    if (!newActionText.trim() || !canAddAction) return;

    const newAction = {
      action: newActionText,
      finalResponse: "", // will be updated by backend
      status: "running",
    };

    try {
      const res = await fetchWithHandling(`${API_URL}/actions/email/${isOutlook ? "microsoft_outlook" : "gmail"}/${messageId}/actions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${userGoogleId}`
        },
        body: JSON.stringify(newAction),
      });
      // if (!res.ok) {
      //   console.error("Error adding action");
      //   return;
      // }
      setNewActionText("");
      toast.success("Action Created")

    } catch (error) {
      console.error(error);
      toast.error(error)
    } finally {
      setLoading(false);
      fetchActions(); // refresh actions list after adding
    }
  };

  const deleteAction = async (index) => {
    try {
      setLoading(true);
      await fetchWithHandling(`${API_URL}/actions/email/${isOutlook ? "microsoft_outlook" : "gmail"}/${messageId}/actions/${index}`, {
        method: "DELETE",
        headers: {
          "Authorization": `Bearer ${userGoogleId}`
        }
      });
      // if (!res.ok) {
      //   console.error("Error deleting action");
      //   return;
      // }
      toast.success("Action Deleted")

    } catch (error) {
      console.error(error);
      toast.error(error)
    } finally {
      setLoading(false);
      fetchActions(); // refresh after deletion
    }
  };

  // Render an icon based on the status.
  const renderStatusIcon = (status) => {
    switch (status) {
      case "running":
        return <FaSpinner className="animate-spin text-[#5652CF]" />;
      case "success":
        return <FaCheckCircle className="text-green-500" />;
      case "failed":
        return <FaTimesCircle className="text-red-500" />;
      case "approvalNeeded":
        return <FaExclamationCircle className="text-yellow-500" />;
      default:
        return null;
    }
  };

  // Update decision for a given action's approval email.
  // Also include the original email_arguments from the backend.
  const handleApprovalDecision = (actionIndex, emailKey, approved, emailArguments) => {
    setApprovalDecisions((prev) => ({
      ...prev,
      [actionIndex]: {
        ...prev[actionIndex],
        [emailKey]: {
          email_arguments: emailArguments,
          approved,
          disapproved: !approved,
          disapprovalReason: approved ? "" : (prev[actionIndex]?.[emailKey]?.disapprovalReason || "")
        }
      }
    }));
  };

  // Update the disapproval reason for a given approval email.
  const handleDisapprovalReasonChange = (actionIndex, emailKey, value) => {
    setApprovalDecisions((prev) => ({
      ...prev,
      [actionIndex]: {
        ...prev[actionIndex],
        [emailKey]: {
          ...prev[actionIndex]?.[emailKey],
          disapprovalReason: value
        }
      }
    }));
  };

  // Submit the complete approval decisions for an action.
  const submitApproval = async (actionIndex) => {
    setLoading(true);
    const decisions = approvalDecisions[actionIndex];
    if (!decisions) return;
    try {
      const res = await fetchWithHandling(`${API_URL}/actions/email/${isOutlook ? "microsoft_outlook" : "gmail"}/${messageId}/actions/${actionIndex}/approval-update`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${userGoogleId}`
        },
        // The endpoint expects a key "approvalNeededEmails"
        body: JSON.stringify({ approvalNeededEmails: decisions }),
      });
      // if (!res.ok) {
      //   console.error("Error submitting approval decisions");
      //   return;
      // }
      // Clear decisions and refresh actions list after successful submission
      setApprovalDecisions((prev) => ({ ...prev, [actionIndex]: {} }));
      fetchActions();
      toast.success("Email Approved")
    } catch (error) {
      console.error(error);
      toast.error(error)
    } finally {
      setLoading(false);
    }
  };

  // Check if all email decisions for an action are valid:
  // Each email must have a decision, and if disapproved must include a non-empty disapproval reason.
  const isApprovalReady = (actionIndex, approvalEmails) => {
    return Object.entries(approvalEmails).every(([key]) => {
      const decision = approvalDecisions[actionIndex]?.[key];
      if (!decision) return false;
      if (decision.approved === true) return true;
      if (
        decision.approved === false &&
        decision.disapproved === true &&
        decision.disapprovalReason &&
        decision.disapprovalReason.trim() !== ""
      ) {
        return true;
      }
      return false;
    });
  };

  return (
    <div className="bg-white shadow-lg p-6 rounded-xl w-full transition-all duration-300 hover:shadow-2xl overflow-hidden">
      <h2 className="text-lg font-bold text-gray-800 mb-6">Actions</h2>

      {/* List of actions from the backend */}
      {actions.map((item, index) => (
        <div
          key={index}
          className="border border-gray-200 rounded-xl mb-4 transition transform hover:shadow-md overflow-hidden"
        >
          {/* Header Section */}
          <div
            className="flex items-center justify-between p-4 cursor-pointer bg-gradient-to-r from-zinc-200 to-zinc-100"
            onClick={() => toggleItem(index)}
          >
            <div className="flex items-center gap-3">
              <span >{renderStatusIcon(item.status)}</span>
              <span className="font-semibold  text-sm text-gray-700">{item.action}</span>
            </div>
            <div className="flex items-center gap-3">
              <span
                className={`text-sm font-medium ${item.status === "running"
                    ? "text-[#5652CF]"
                    : item.status === "success"
                      ? "text-green-600"
                      : item.status === "failed"
                        ? "text-red-500"
                        : "text-yellow-500"
                  }`}
              >
                {item.status.charAt(0).toUpperCase() + item.status.slice(1)}
              </span>
              {(item.status === "success" || item.status === "failed") && (
                <MdArrowDropDown
                  className={`text-gray-500 transition-transform duration-300 ${openItems[index] ? 'rotate-180' : 'rotate-0'}`}
                />
              )}
              <button disabled={loading} onClick={(e) => { e.stopPropagation(); deleteAction(index); }}>
                <FiTrash2 className="text-red-400 cursor-pointer hover:text-red-700 transition-colors" />
              </button>
            </div>
          </div>

          {/* Body Section for details */}
          {openItems[index] && (item.status === "success" || item.status === "failed") && (
            <div className="p-4 border-t border-gray-200 bg-gray-50 text-gray-600 text-sm">
              <MarkdownRenderer lightTheme={true} markdown={item.finalResponse || item.error} userGoogleId={userGoogleId} />
            </div>
          )}

          {/* Detailed Approval UI for approvalNeeded actions */}
          {item.status === "approvalNeeded" && (
            <div className="p-4 border-t border-yellow-200 bg-yellow-50 text-gray-700 text-sm">
              {item.approvalNeededEmails && Object.entries(item.approvalNeededEmails).map(([emailKey, emailData]) => {
                // Parse email_arguments JSON string
                let parsedEmail = {};
                try {
                  parsedEmail = JSON.parse(emailData.email_arguments);
                } catch (err) {
                  console.error("Error parsing email_arguments", err);
                }
                // Retrieve any existing decision (if any)
                const decision = approvalDecisions[index]?.[emailKey] || {};
                return (
                  <div key={emailKey} className="mb-4 p-3 border rounded-md bg-white">
                    <p className="font-semibold">Subject: {parsedEmail.subject}</p>
                    <div className="mt-1">
                      <span className="font-semibold">Body: </span>
                      <span dangerouslySetInnerHTML={{ __html: parsedEmail.body }} />
                    </div>
                    <p className="mt-1">
                      <span className="font-semibold">Recipients:</span> {parsedEmail.recipients && parsedEmail.recipients.join(", ")}
                    </p>
                    <div className="mt-2 flex items-center gap-2">
                      <button
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${decision.approved === true ? 'bg-green-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                        onClick={() => handleApprovalDecision(index, emailKey, true, emailData.email_arguments)}
                      >
                        Approve
                      </button>
                      <button
                        className={`px-3 py-1 rounded-md text-sm transition-colors ${decision.approved === false ? 'bg-red-500 text-white' : 'bg-gray-200 hover:bg-gray-300'}`}
                        onClick={() => handleApprovalDecision(index, emailKey, false, emailData.email_arguments)}
                      >
                        Disapprove
                      </button>
                      {decision.approved === false && (
                        <input
                          type="text"
                          placeholder="Disapproval reason"
                          value={decision.disapprovalReason || ""}
                          onChange={(e) => handleDisapprovalReasonChange(index, emailKey, e.target.value)}
                          className="ml-2 border rounded-md p-1 text-sm"
                        />
                      )}
                    </div>
                  </div>
                );
              })}
              {/* Enable the Submit Approval button only if all decisions are valid */}
              {item.approvalNeededEmails &&
                Object.entries(item.approvalNeededEmails).length > 0 &&
                isApprovalReady(index, item.approvalNeededEmails) && (
                  <button
                    disabled={loading}
                    onClick={() => submitApproval(index)}
                    className="mt-4 px-4 py-2 bg-[#5652CF] hover:bg-blue-600 text-white rounded-md text-sm"
                  >
                    Submit Approval
                  </button>
                )}
            </div>
          )}
        </div>
      ))}

      {/* Quick Action Items */}
      {actionItems.length > 0 && (
        <div className="mt-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">Quick Actions</h3>
          <div className="flex flex-wrap gap-2">
            {actionItems.map((action, idx) => (
              <button
                key={idx}
                onClick={() => setNewActionText(action.task)}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-3 py-1 rounded-md text-sm transition-colors"
              >
                {action.task}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input to add a new action */}
      <div className="mt-6 flex items-center gap-3">
        <input
          type="text"
          value={newActionText}
          onChange={(e) => setNewActionText(e.target.value)}
          placeholder="Enter new action"
          className="flex-1 border text-gray-800 border-gray-300 rounded-xl p-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 transition"
          disabled={!canAddAction}
        />
        <button
          onClick={addAction}

          className={`px-5 py-3 rounded-xl text-white text-sm font-semibold transition-colors ${canAddAction && newActionText.trim()
              ? "bg-[#5652CF] hover:bg-blue-600"
              : "bg-gray-400 cursor-not-allowed"
            }`}
          disabled={loading || !canAddAction || !newActionText.trim()}
        >
          Execute
        </button>
      </div>
      {!canAddAction && (
        <p className="text-red-500 text-sm mt-4">
          Please wait until the last action is successful before adding a new action.
        </p>
      )}
    </div>
  );
}